// @ts-nocheck
/**
 * Team summary — one team's whole weekend, on one page.
 *
 * A coach rings and asks when they play. This is the page a director pulls up
 * to answer: who the team is, every game in time order (including the ones
 * with no time yet), what they asked for, and every problem the pre-publish
 * check already names about them — in the check's own words, taken from
 * _shared/schedule-invariants.ts and never recomputed here.
 *
 * The page writes nothing. Print gives one clean page per team.
 */
import * as React from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { invokeEdgeFunction } from '@/lib/invokeEdgeFunction';
import { SchedulingPageShell } from '@/components/admin/schedule/SchedulingPageShell';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { formatCalendarDate, formatTime } from '@/lib/dates';
import { Printer, Copy, Star, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { subscribeToScheduleChanges } from '@/lib/scheduleBus';

const dayLabel = (d?: string | null) => (d ? formatCalendarDate(d, 'EEE d MMM', d) : null);

const poolShort = (p?: string | null) =>
  p ? String(p).replace(/^pool\s+/i, '') : '—';

const roundOrPool = (g: any) =>
  g.pool_name ? `Pool ${String(g.pool_name).replace(/^pool\s+/i, '')}` : (g.bracket_label || '—');

const gameTypeLabel = (g: any) => {
  const type = String(g?.game_type ?? '').toLowerCase();
  if (type === 'pool') return 'Pool';
  if (type === 'bracket') return 'Bracket';
  if (type === 'crossover') return 'Crossover';
  if (type === 'consolation') return 'Consolation';
  return type ? type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : 'Game';
};

const opponentLabel = (g: any) => {
  const opponent = String(g?.opponent ?? '').trim();
  return opponent && opponent.toUpperCase() !== 'TBD' ? opponent : 'Bye';
};

const compactTime = (value?: string | null) => formatTime(value).replace(/am$/, 'a').replace(/pm$/, 'p');

export const shortOpponentLabel = (opponent: string, division?: string | null) => {
  const full = String(opponent ?? '').trim();
  if (!full || /^(bye|winner of game|loser of game)/i.test(full)) return full || 'Bye';
  const div = String(division ?? '').trim();
  if (!div) return full;
  const escaped = div.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return full.replace(new RegExp(`^${escaped}(?:\\s*[-–—·:]\\s*|\\s+)`, 'i'), '') || full;
};

export const opponentChipText = (game: any, division?: string | null) => {
  const when = game?.scheduled_date && game?.scheduled_start_time
    ? `${formatCalendarDate(game.scheduled_date, 'EEE', '')} ${compactTime(game.scheduled_start_time)}`
    : 'Not scheduled';
  return `${when} v ${shortOpponentLabel(opponentLabel(game), division)}`;
};

const gameLine = (g: any) => {
  const when = g.scheduled_date && g.scheduled_start_time
    ? `${dayLabel(g.scheduled_date)} ${formatTime(g.scheduled_start_time)}`
    : 'Not scheduled yet';
  return `Game ${g.game_number ?? '—'}  ${gameTypeLabel(g)}  ${when}  ${g.court_name ?? '—'}  v ${opponentLabel(g)}`;
};

function TeamGameRow({ game }: { game: any }) {
  const date = game.scheduled_date ? dayLabel(game.scheduled_date) : 'Not scheduled yet';
  const time = game.scheduled_start_time ? formatTime(game.scheduled_start_time) : '';
  const court = game.court_name ?? '—';
  return (
    <div
      role="row"
      data-testid="team-game-row"
      className="grid w-full gap-1 border-b py-2 text-sm last:border-0 sm:grid-cols-[5rem_7rem_5rem_minmax(8rem,1fr)_6rem] sm:items-center sm:gap-3"
    >
      <div className="font-medium sm:whitespace-nowrap">Game {game.game_number ?? '—'}</div>
      <div className="text-muted-foreground sm:whitespace-nowrap">{date}</div>
      <div className="text-muted-foreground sm:whitespace-nowrap">{time}</div>
      <div className="min-w-0">
        <span className="text-muted-foreground">{court}</span>
        <span className="mx-2 text-muted-foreground">·</span>
        <span>v {opponentLabel(game)}</span>
      </div>
      <div className="text-muted-foreground sm:text-right sm:whitespace-nowrap">{gameTypeLabel(game)}</div>
    </div>
  );
}

function TeamSheet({ team, eventName, eventId }: { team: any; eventName?: string; eventId: string }) {
  const s = team.summary ?? {};
  const target = s.target ?? null;
  const errors = (team.findings ?? []).filter((f: any) => f.level === 'error');
  const warnings = (team.findings ?? []).filter((f: any) => f.level === 'warning');

  return (
    <section className="team-sheet w-full space-y-5 break-after-page">
      <header className="space-y-1 border-b pb-3">
        <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          {team.is_priority && <Star className="h-5 w-5 fill-current text-amber-500" aria-label="Priority team" />}
          {team.name}
        </h2>
        <div className="text-sm text-muted-foreground">
          {[team.division, team.pool ? `Pool ${String(team.pool).replace(/^pool\s+/i, '')}` : null]
            .filter(Boolean).join(' · ') || 'No division'}
          {' · '}
          {team.checked_in_at ? 'Checked in' : 'Not checked in'}
        </div>
        <div className="text-sm">
          {team.coach?.name || team.coach?.email || team.coach?.phone
            ? [team.coach?.name, team.coach?.email, team.coach?.phone].filter(Boolean).join(' · ')
            : 'No coach on file'}
          {eventName ? <span className="text-muted-foreground"> — {eventName}</span> : null}
        </div>
      </header>

      <div className="rounded-md border p-3 text-sm space-y-1">
        <div className="font-medium">
          {s.games} game{s.games === 1 ? '' : 's'}
          {target && s.games !== target ? `. The event promised ${target}.` : ''}
        </div>
        {(s.per_day ?? []).length ? (
          <div>
            Games per day: {s.per_day.map((d: any) => `${dayLabel(d.date)} ${d.count}`).join(' · ')}
            {(team.findings ?? []).some((f: any) => f.kind === 'daily_cap') && (
              <span className="ml-2 font-medium text-amber-700">over the games-per-day limit</span>
            )}
          </div>
        ) : null}
        {s.shortest_gap_minutes != null ? (
          <div>
            Shortest gap between games: {s.shortest_gap_minutes} min
            {(team.findings ?? []).some((f: any) => ['back_to_back', 'rest_gap'].includes(f.kind)) && (
              <span className="ml-2 font-medium text-amber-700">below the minimum rest</span>
            )}
          </div>
        ) : null}
      </div>

      <div>
        <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Games</h3>
        {(team.games ?? []).length === 0 ? (
          <p className="text-sm">No games scheduled for this team yet.</p>
        ) : (
          <div role="table" className="w-full">
            {team.games.map((g: any) => <TeamGameRow key={g.id} game={g} />)}
          </div>
        )}
      </div>

      <div>
        <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Requests</h3>
        {(team.requests ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground">This team asked for nothing.</p>
        ) : (
          <ul className="space-y-1 text-sm">
            {team.requests.map((r: any) => (
              <li key={r.id}>
                {r.asked}{' '}
                {r.honored === false
                  ? <span className="font-medium text-amber-700">Not kept by this schedule.</span>
                  : r.honored === true
                    ? <span className="text-muted-foreground">Kept.</span>
                    : <span className="text-muted-foreground">{r.status}.</span>}
                {r.fee_charged > 0 ? ` Paid $${r.fee_charged}.` : ''}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Problems</h3>
        {errors.length + warnings.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nothing wrong with this team's schedule.</p>
        ) : (
          <ul className="space-y-2 text-sm">
            {[...errors, ...warnings].map((f: any, i: number) => (
              <li key={i} className="flex flex-wrap items-baseline gap-2">
                <span className={f.level === 'error' ? 'text-destructive' : 'text-amber-700'}>
                  {f.level === 'error' ? '●' : '⚠'}
                </span>
                <span>{f.message ?? f.title}</span>
                {(f.games ?? []).map((row: any) => (
                  <Link
                    key={row.game_id}
                    className="no-print underline underline-offset-2"
                    to={`/admin-dashboard/events/${eventId}/scheduling/grid?focus=${row.game_id}`}
                  >
                    Game {row.game_number ?? '—'}
                  </Link>
                ))}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

/**
 * Every team in the event, one row each — the landing state.
 *
 * Nothing here is computed: each row reads the same fields the detail block
 * prints. One read serves both, so the table costs no extra request.
 */
function AllTeamsTable({
  teams,
  onOpen,
  scrollRef,
}: {
  teams: any[];
  onOpen: (t: any) => void;
  scrollRef: React.MutableRefObject<HTMLDivElement | null>;
}) {
  return (
    <div
      ref={scrollRef}
      data-testid="all-teams-table"
      className="min-h-0 w-full flex-1 overflow-y-auto overflow-x-hidden"
    >
      <table className="block w-full border-collapse text-sm sm:table">
        <thead className="hidden bg-background sm:sticky sm:top-0 sm:z-10 sm:table-header-group">
          <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
            <th className="px-3 py-2 font-semibold">Team</th>
            <th className="px-3 py-2 font-semibold">Division</th>
            <th className="px-3 py-2 font-semibold">Pool</th>
            <th className="px-3 py-2 font-semibold">Checked in</th>
            <th className="px-3 py-2 font-semibold">Games</th>
            <th className="px-3 py-2 font-semibold">Opponents</th>
            <th className="px-3 py-2 font-semibold">Problems</th>
          </tr>
        </thead>
        <tbody className="block sm:table-row-group">
          {teams.length === 0 ? (
            <tr>
              <td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">
                No teams match these filters.
              </td>
            </tr>
          ) : teams.map((t) => {
            const s = t.summary ?? {};
            const target = s.target ?? null;
            const games = s.games ?? 0;
            const short = target != null && games < target;
            const errors = (t.findings ?? []).filter((f: any) => f.level === 'error').length;
            const warnings = (t.findings ?? []).filter((f: any) => f.level === 'warning').length;
            return (
              <tr
                key={t.registration_id}
                tabIndex={0}
                role="button"
                onClick={() => onOpen(t)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(t); } }}
                className="grid cursor-pointer grid-cols-2 gap-x-3 gap-y-1 border-b px-3 py-3 last:border-0 hover:bg-muted/50 focus:bg-muted/50 focus:outline-none sm:table-row sm:px-0 sm:py-0"
              >
                <td className="col-span-2 font-medium sm:table-cell sm:px-3 sm:py-2">
                  <span className="flex items-center gap-1.5">
                    {t.is_priority && <Star className="h-3.5 w-3.5 fill-current text-amber-500" aria-label="Priority team" />}
                    {t.name}
                  </span>
                </td>
                <td className="text-muted-foreground before:mr-1 before:content-['Division:'] sm:table-cell sm:px-3 sm:py-2 sm:text-foreground sm:before:content-none">{t.division ?? '—'}</td>
                <td className="text-muted-foreground before:mr-1 before:content-['Pool:'] sm:table-cell sm:px-3 sm:py-2 sm:text-foreground sm:before:content-none">{poolShort(t.pool)}</td>
                <td className="text-muted-foreground before:mr-1 before:content-['Checked_in:'] sm:table-cell sm:px-3 sm:py-2 sm:text-foreground sm:before:content-none">{t.checked_in_at ? 'Yes' : 'No'}</td>
                <td className="whitespace-nowrap text-muted-foreground before:mr-1 before:content-['Games:'] sm:table-cell sm:px-3 sm:py-2 sm:text-foreground sm:before:content-none">
                  <span className={short ? 'font-semibold text-destructive' : ''}>{games}</span>
                  {target != null && games !== target ? (
                    <span className={short ? 'text-destructive' : 'text-muted-foreground'}> of {target}</span>
                  ) : null}
                </td>
                <td className="col-span-2 sm:table-cell sm:max-w-[42rem] sm:px-3 sm:py-2">
                  <div className="flex flex-wrap gap-1" data-testid="opponent-chips">
                    {(t.games ?? []).map((game: any) => {
                      const fullOpponent = opponentLabel(game);
                      return (
                        <span
                          key={game.id}
                          title={fullOpponent}
                          className="inline-flex max-w-full items-center gap-1 rounded border bg-muted/50 px-1.5 py-1 text-[11px] leading-tight text-foreground"
                        >
                          {opponentChipText(game, t.division)}
                          {/* The word, in red — never a different chip colour. */}
                          {String(game.game_type ?? '').toLowerCase() === 'crossover' && (
                            <span className="font-semibold text-red-600">Crossover</span>
                          )}
                        </span>
                      );
                    })}
                    {!(t.games ?? []).length && <span className="text-xs text-muted-foreground">No games</span>}
                  </div>
                </td>
                <td className="whitespace-nowrap text-muted-foreground before:mr-1 before:content-['Problems:'] sm:table-cell sm:px-3 sm:py-2 sm:text-foreground sm:before:content-none">
                  {errors > 0 && <span className="mr-2 text-destructive">● {errors}</span>}
                  {warnings > 0 && <span className="text-amber-700">⚠ {warnings}</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function OperatorEventTeamSummaryPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const [params, setParams] = useSearchParams();
  const [data, setData] = React.useState<any>(null);
  // Two reads, started together. The list of teams is one query and comes back
  // quickly; the weekend itself needs the publish check and every bracket, and
  // takes seconds. Waiting for the second to name the first is what made the
  // page look broken.
  const [listData, setListData] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(true);
  const [listLoading, setListLoading] = React.useState(true);
  const [printAll, setPrintAll] = React.useState(false);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [attempt, setAttempt] = React.useState(0);

  React.useEffect(() => {
    let live = true;
    setListLoading(true);
    setLoading(true);

    (async () => {
      const { data: res } = await invokeEdgeFunction('rle-operator-schedule-manual', {
        body: { action: 'team_summary_teams', event_id: eventId },
      });
      if (!live) return;
      setListData(res?.data ?? null);
      setListLoading(false);
    })();

    (async () => {
      const { data: res, error } = await invokeEdgeFunction('rle-operator-schedule-manual', {
        body: { action: 'team_summary', event_id: eventId },
      });
      if (!live) return;
      if (error || !res?.ok) {
        const msg = error?.message || 'Could not load this event’s teams.';
        setLoadError(msg);
        toast.error(msg);
      } else {
        setLoadError(null);
      }
      setData(res?.data ?? null);
      setLoading(false);
    })();

    return () => { live = false; };
  }, [eventId, attempt]);

  // Moving a game on the grid writes straight away, so this page refetches the
  // moment it hears about it — no reload, and no figure left behind the board.
  React.useEffect(() => {
    const refresh = () => setAttempt((n) => n + 1);
    const stop = subscribeToScheduleChanges(eventId, refresh);
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      stop();
      window.removeEventListener('focus', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [eventId]);


  const fullTeams: any[] = data?.teams ?? [];
  // The picker draws from whichever list has arrived; the sheet only ever
  // draws from the full read.
  const teams: any[] = fullTeams.length ? fullTeams : (listData?.teams ?? []);

  // The id the link asked for is remembered the first time it is seen, so a
  // later render — a remount, or something else rewriting the address bar —
  // cannot lose it while the team list is still loading.
  const askedNow = (params.get('team') || params.get('teamId') || params.get('registration_id') || '').trim();
  const askedRef = React.useRef('');
  if (askedNow && askedRef.current !== askedNow) askedRef.current = askedNow;
  const [pickedId, setPickedId] = React.useState('');
  const asked = (pickedId || askedNow || askedRef.current).toLowerCase();

  React.useEffect(() => {
    if (loading) return;
    console.info('[Team summary diagnostic]', {
      pathname: window.location.pathname,
      search: window.location.search,
      expectedParameter: 'team',
      receivedParameter: askedNow || null,
      optionCount: teams.length,
      options: teams.map((t) => ({
        registration_id: t.registration_id ?? null,
        recruitlook_team_id: t.recruitlook_team_id ?? null,
        team_id: t.team_id ?? null,
      })),
    });
  }, [askedNow, loading, teams]);

  // The server times every part of the weekend read and sends the rows back.
  // One line per part, in milliseconds, so a slow page says where the time went
  // instead of being guessed at.
  React.useEffect(() => {
    const rows = (data as any)?.timings;
    if (!Array.isArray(rows) || !rows.length) return;
    console.info(
      '[Team summary server timings] RecruitLook lookups this request:',
      (data as any)?.recruitlook_lookups ?? 0,
    );
    for (const row of rows) console.info(`[Team summary server timings] ${row.part} ${row.ms}ms`);
  }, [data]);


  // No team asked for means the page shows every team — never a silent
  // fallback to whichever team happens to be first.
  const matchIn = (list: any[]) =>
    (asked
      ? list.find(
          (t) =>
            String(t.registration_id ?? '').toLowerCase() === asked ||
            String(t.recruitlook_team_id ?? '').toLowerCase() === asked,
        )
      : null) ?? null;

  // What the picker says (may come from the fast list).
  const team = matchIn(teams);
  // What the sheet draws (always the full read).
  const fullTeam = fullTeams.length ? matchIn(fullTeams) : null;
  // The picker shows what is actually on screen, never an id that matches nothing.
  const selectedId = team?.registration_id ?? '';
  const selectedLabel = team ? `${team.name}${team.division ? ` · ${team.division}` : ''}` : '';

  // ---- The all-teams table: filters, sort, and a kept scroll position ----
  const [fDivision, setFDivision] = React.useState('all');
  const [fPool, setFPool] = React.useState('all');
  const [fCheckedIn, setFCheckedIn] = React.useState('all');
  const [fProblems, setFProblems] = React.useState('all');
  const tableScrollRef = React.useRef<HTMLDivElement | null>(null);
  const keptScroll = React.useRef(0);

  const divisions = React.useMemo(
    () => [...new Set(fullTeams.map((t) => t.division).filter(Boolean))].sort(),
    [fullTeams],
  );
  const pools = React.useMemo(
    () => [...new Set(fullTeams.map((t) => poolShort(t.pool)).filter((p) => p && p !== '—'))].sort(),
    [fullTeams],
  );

  const tableTeams = React.useMemo(() => {
    const rows = fullTeams.filter((t) => {
      if (fDivision !== 'all' && t.division !== fDivision) return false;
      if (fPool !== 'all' && poolShort(t.pool) !== fPool) return false;
      if (fCheckedIn === 'yes' && !t.checked_in_at) return false;
      if (fCheckedIn === 'no' && t.checked_in_at) return false;
      if (fProblems === 'yes' && !(t.findings ?? []).length) return false;
      if (fProblems === 'no' && (t.findings ?? []).length) return false;
      return true;
    });
    return rows.sort(
      (a, b) =>
        String(a.division ?? '').localeCompare(String(b.division ?? ''), undefined, { numeric: true }) ||
        String(poolShort(a.pool)).localeCompare(String(poolShort(b.pool)), undefined, { numeric: true }) ||
        String(a.name ?? '').localeCompare(String(b.name ?? '')),
    );
  }, [fullTeams, fDivision, fPool, fCheckedIn, fProblems]);

  const openTeam = (t: any) => {
    keptScroll.current = tableScrollRef.current?.scrollTop ?? 0;
    setPickedId(t.registration_id);
    setParams((p) => { p.set('team', t.registration_id); return p; }, { replace: true });
  };

  const backToAllTeams = () => {
    setPickedId('');
    askedRef.current = '';
    setParams((p) => { p.delete('team'); p.delete('teamId'); p.delete('registration_id'); return p; }, { replace: true });
  };

  const showTable = !fullTeam && !printAll;

  // Coming back from a team lands where he left the list.
  React.useEffect(() => {
    if (showTable && tableScrollRef.current) tableScrollRef.current.scrollTop = keptScroll.current;
  }, [showTable]);



  const printOne = () => { setPrintAll(false); setTimeout(() => window.print(), 50); };
  const printEveryTeam = () => { setPrintAll(true); setTimeout(() => { window.print(); setPrintAll(false); }, 50); };

  const copyForEmail = async () => {
    if (!fullTeam) return;
    const text = [
      `${fullTeam.name}${fullTeam.division ? ` — ${fullTeam.division}` : ''}`,
      ...(fullTeam.games ?? []).map(gameLine),
    ].join('\n');
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Games copied — paste it into an email.');
    } catch {
      toast.error('Could not copy. Select the list and copy it by hand.');
    }
  };

  return (
    <SchedulingPageShell
      title="Team summary"
      subtitle={data?.event?.name ?? listData?.event?.name}
      actions={
        <div className="no-print flex flex-wrap items-center gap-2">
          {fullTeam && (
            <Button size="sm" variant="outline" onClick={backToAllTeams} data-testid="back-to-all-teams">
              <ArrowLeft className="mr-2 h-4 w-4" /> All teams
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={copyForEmail} disabled={!fullTeam}>

            <Copy className="mr-2 h-4 w-4" /> Copy for email
          </Button>
          <Button size="sm" variant="outline" onClick={printOne} disabled={!fullTeam}>
            <Printer className="mr-2 h-4 w-4" /> Print
          </Button>
          <Button size="sm" variant="outline" onClick={printEveryTeam} disabled={!fullTeams.length}>
            <Printer className="mr-2 h-4 w-4" /> Print all teams
          </Button>
          <Button size="sm" variant="outline" asChild>
            <Link to={`/admin-dashboard/events/${eventId}/scheduling/grid`}>
              <ArrowLeft className="mr-2 h-4 w-4" /> Grid
            </Link>
          </Button>
        </div>
      }
      controlRow={
        <div className="no-print flex w-full flex-col gap-2">
        <div className="flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
          <span className="text-sm text-muted-foreground">Team</span>

          <Select
            value={selectedId}
            disabled={listLoading && !teams.length}
            onValueChange={(v) => {
              setPickedId(v);
              setParams((p) => { p.set('team', v); return p; }, { replace: true });
            }}
          >
            <SelectTrigger className="h-9 w-full sm:w-[320px]" data-testid="team-picker">
              {/* "Pick a team" means choose one. While the list is still coming
                  it must say something else, or a loading page reads exactly
                  like a broken one. The label is drawn from the team on screen,
                  so it never depends on the dropdown having been opened once. */}
              <SelectValue placeholder={listLoading && !teams.length ? 'Loading teams…' : 'Pick a team'}>
                {selectedLabel || undefined}
              </SelectValue>
            </SelectTrigger>

            <SelectContent className="bg-popover z-50">
              {teams.map((t) => (
                <SelectItem key={t.registration_id} value={t.registration_id}>
                  {t.name}{t.division ? ` · ${t.division}` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {team?.is_priority && <Badge variant="secondary">Priority</Badge>}
        </div>

        {/* Filters, always on show — same pattern as the Grid. */}
        <div className="grid w-full grid-cols-1 gap-2 sm:flex sm:flex-wrap sm:items-center" data-testid="team-summary-filters">
          <Select value={fDivision} onValueChange={setFDivision}>
            <SelectTrigger className="h-8 w-full sm:w-[150px]" aria-label="Division"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-popover z-50">
              <SelectItem value="all">All divisions</SelectItem>
              {divisions.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={fPool} onValueChange={setFPool}>
            <SelectTrigger className="h-8 w-full sm:w-[130px]" aria-label="Pool"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-popover z-50">
              <SelectItem value="all">All pools</SelectItem>
              {pools.map((p) => <SelectItem key={p} value={p}>Pool {p}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={fCheckedIn} onValueChange={setFCheckedIn}>
            <SelectTrigger className="h-8 w-full sm:w-[160px]" aria-label="Checked in"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-popover z-50">
              <SelectItem value="all">Checked in or not</SelectItem>
              <SelectItem value="yes">Checked in</SelectItem>
              <SelectItem value="no">Not checked in</SelectItem>
            </SelectContent>
          </Select>
          <Select value={fProblems} onValueChange={setFProblems}>
            <SelectTrigger className="h-8 w-full sm:w-[170px]" aria-label="Problems"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-popover z-50">
              <SelectItem value="all">With or without problems</SelectItem>
              <SelectItem value="yes">Has problems</SelectItem>
              <SelectItem value="no">No problems</SelectItem>
            </SelectContent>
          </Select>
          {showTable && !loading && (
            <span className="text-sm text-muted-foreground">
              {tableTeams.length} team{tableTeams.length === 1 ? '' : 's'}
            </span>
          )}
        </div>
        </div>
      }
      fill={showTable}
    >
      <div
        className={showTable ? 'flex min-h-0 w-full flex-1 flex-col' : 'w-full'}
        data-testid="team-summary"
      >
        {loading ? (
          <div className="space-y-3 p-4" data-testid="team-summary-loading">
            <p className="text-sm text-muted-foreground">
              {team ? `Loading ${team.name}'s weekend…` : 'Loading this event\u2019s teams…'}
            </p>
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-40 w-full" />
          </div>
        ) : loadError ? (
          // A failed read used to look exactly like an event with no teams.
          // Say which it is, and offer the way out.
          <div className="space-y-3 p-4" data-testid="team-summary-error">
            <p className="text-sm text-destructive">{loadError}</p>
            <Button size="sm" variant="outline" onClick={() => setAttempt((a) => a + 1)}>Retry</Button>
          </div>
        ) : !fullTeams.length ? (
          <p className="p-4 text-sm text-muted-foreground">No teams are registered for this event yet.</p>

        ) : printAll ? (
          fullTeams.map((t) => (
            <TeamSheet key={t.registration_id} team={t} eventName={data?.event?.name} eventId={eventId!} />
          ))
        ) : fullTeam ? (
          <TeamSheet team={fullTeam} eventName={data?.event?.name} eventId={eventId!} />
        ) : (
          <AllTeamsTable teams={tableTeams} onOpen={openTeam} scrollRef={tableScrollRef} />
        )}
      </div>
    </SchedulingPageShell>
  );
}
